
import React, {useRef, useState } from 'react';
import {
  DataGrid
} from 'devextreme-react/data-grid';
import { t } from 'i18next';
import { Popup } from 'devextreme-react/popup';
import { Button, LoadPanel, Switch, TextArea } from 'devextreme-react';
import { accountRelationService } from "@/projects/admin/services/account-relation-service";
import { accountService } from "@/projects/admin/services/account-service";
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import { toast } from 'react-toastify';
import { ApprovalRequestTimeline } from '_metronic/partials/layout/approval-request/approval-request-history';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';


const App = () => {
  const [loadPanelVisible, setLoadPanelVisible] = useState(false);
  const dataGridRef = useRef<DataGrid>(null);
  const commentAreaRef = useRef<TextArea>(null);
  const [approvalHistoryPopupVisible, setApprovalHistoryPopupVisible] = useState(false);
  const [currentApprovalSteps, setCurrentApprovalSteps] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);



  const [retailerStore] = useState(createCustomStore({
    key: 'id',
    load: accountService.lookupAccounts,
    byKey: (key, loadOptions) => {
      if (key) {
        loadOptions.filter = ["id", "=", key];
      }
    },
    onLoading(loadOptions) {
      loadOptions.filter = ["AccountType", "=", "Retailer"]
    },
  }));

  const [supplierStore] = useState(createCustomStore({
    key: 'id',
    load: accountService.lookupAccounts,
    byKey: (key, loadOptions) => {
      if (key) {
        loadOptions.filter = ["id", "=", key];
      }
    },
    onLoading(loadOptions) {
      loadOptions.filter = ["AccountType", "=", "Supplier"]
    },
  }));

  const onApprovalHistoryPopupHiding = () => {
    setSelectedRequest(null);
    setApprovalHistoryPopupVisible(false);
  };

  const handleRowUpdating = async (e: any) => {
    e.newData = { ...e.oldData, ...e.newData }
  };


  const store = createCustomStore({
    key: ["id"],
    load: accountRelationService.getRelationRequests,
    byKey: (key, loadOptions) => {
      if (key) {
        loadOptions.filter = ["id", "=", key];
      }
    },
  });

  const approveRequest = async () => {
    try {
      setLoadPanelVisible(true);
      const comment = commentAreaRef.current?.instance.option("value");
      await accountRelationService.approveAccountRelationRequest({ id: selectedRequest.id, comment })
      setLoadPanelVisible(false);
      setApprovalHistoryPopupVisible(false);
      commentAreaRef.current?.instance.option("value", undefined);
      await dataGridRef.current?.instance.refresh();
    } catch (error: any) {
      console.log(error)
      setLoadPanelVisible(false);
      toast.error(error.response.data.message);
    }
  }

  const rejectRequest = async () => {
    try {
      setLoadPanelVisible(true);
      const comment = commentAreaRef.current?.instance.option("value");
      await accountRelationService.rejectAccountRelationRequest({ id: selectedRequest.id, comment: comment })
      setLoadPanelVisible(false);
      setApprovalHistoryPopupVisible(false);
      commentAreaRef.current?.instance.option("value", undefined);
      await dataGridRef.current?.instance.refresh();
    } catch (error: any) {
      console.log(error)
      setLoadPanelVisible(false);
      toast.error(error.response.data.message);
    }
  }


  return (
    <>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={loadPanelVisible}
        showIndicator={true}
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
        message={t("saving_please_wait")}

      />
      <DataGridWrapper
        props={{ id: 'supplier-management', ref: dataGridRef }}
        permissonCode="Admin.Account.Relationship"
        datasource={store}
        onRowUpdating={handleRowUpdating}
        filterVisible={true}
        headerFilterVisible={true}
        pagerVisible={true}
        remoteOperations={true}
        enableCustomToolbarContent={false}
        editingConfig={{
          mode: 'batch',
          allowAdding: false,
          allowUpdating: false,
          allowDeleting: false
        }}
        columns={[
          {
            dataField: 'retailerAccountId',
            caption: t('retailer'),
            required: true,
            lookup: {
              dataSource: retailerStore,
              valueExpr: "id",
              displayExpr: "name"
            }
          },
          {
            dataField: 'supplierAccountId',
            caption: t('supplier'),
            required: true,
            lookup: {
              dataSource: supplierStore,
              valueExpr: "id",
              displayExpr: "name"
            }
          },
          {
            dataField: 'isLocationShared',
            caption: t('isLocationShared'),
            allowEditing: true,
          },
          {
            dataField: 'defaultStockShare',
            caption: t('defaultStockShare'),
            allowEditing: false,
            width: 270
          },
          {
            dataField: 'defaultSalesShare',
            caption: t('defaultSalesShare'),
            allowEditing: false,
            width: 270
          },
          {
            dataField: 'status',
            caption: t('status'),
            allowFiltering: false,
            allowSorting: false,
            allowEditing: false,
            width: 150
          },
          {
            type: 'buttons',
            width: 110,
            buttons: [
              {
                icon: 'ordersbox',
                hint: t("request_history"),
                visible: ((e: any) => !e.row.isEditing && e.row.data.approvalSteps && e.row.data.approvalSteps.length > 0),
                onClick: (e: any) => {
                  setSelectedRequest(e.row.data);
                  setCurrentApprovalSteps(e.row.data.approvalSteps);
                  setApprovalHistoryPopupVisible(true);
                }
              }
            ]
          }
        ]}
      />

      <Popup
        visible={approvalHistoryPopupVisible}
        onHiding={onApprovalHistoryPopupHiding}
        showTitle={true}
        width={600}
        height={selectedRequest && selectedRequest.statusCode === "Awaiting Approval" ? 500 : 400}
        position={{
          of: window,
          at: 'center',
          my: 'center'
        }}
        title={t("approval_history")}
      >
        <ApprovalRequestTimeline timelines={currentApprovalSteps} />
        {selectedRequest && selectedRequest.statusCode === "Awaiting Approval" ? (<div>
          <TextArea
            ref={commentAreaRef}
            height={70}
            className='mt-10'
            readOnly={false}
            label={t("comment") + ':'}
          />
          <div className='row mt-4 '>
            <div className='col mt-2 mb-2 '>
              <div className='row justify-content-end'>
                <div className='col-auto'>
                  <Button
                    text={t("approve")}
                    type='default'
                    icon='check'
                    onClick={approveRequest}
                  />
                </div>
                <div className='col-auto'>
                  <Button
                    text={t("reject")}
                    type='normal'
                    icon='close'
                    onClick={rejectRequest}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>) : <></>}

      </Popup>
    </>

  );
};

export default App;